import React, { useState, useEffect, useRef, useMemo } from 'react';
import { message, Dropdown, Menu, Spin, Tooltip } from 'antd';
import { Search, ChevronLeft, ChevronRight, X, UserCircle, AlertTriangle, Clock, FileText, CheckCircle, ChevronDown, MessageSquare, Monitor } from 'lucide-react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { unstable_batchedUpdates } from 'react-dom';
import { getBulkReportStatus, getRadiologistWorklist, getStudyDetail, getPrimaryModality } from './StudiesService';
import { actionGetUsers, actionRefreshToken } from '../system/systemAction';
import { openStudyInWorkstation } from '../../utils/workstation';
import { actionCreateSession } from '../studyList/StudyListAction';
import { SESSION_TYPE } from '../../utils/constants/config';
import { readNotifications, dismissNotification, NOTIFICATION_UPDATED_KEY } from '../../utils/notificationStore';
import './Studies.scss';

// ─── Phase-1 sample data ─────────────────────────────────────────────────────
const SAMPLE_WORKLIST = [
  {
    id: 1, alert: true,
    priority: 'STAT', priorityColor: 'red',
    patientName: '*tesfa alem', patientId: '630586',
    age: 45, sex: 'M', studyDate: '2026-02-09 11:25',
    modality: 'DX', bodyPart: 'CHEST', images: 3,
    priors: 2, refPhysician: 'Dr. Alemu T.',
    tatProgress: 95, tatLabel: 'Overdue', tatColor: 'red',
    reportStatus: 'No Report', reportStatusColor: 'gray',
  },
  {
    id: 2, alert: false,
    priority: 'Urgent', priorityColor: 'amber',
    patientName: 'Ngsti Hadush**', patientId: 'PAT004947',
    age: 34, sex: 'F', studyDate: '2025-12-18 13:38',
    modality: 'CR', bodyPart: 'KNEE', images: 1,
    priors: 0, refPhysician: 'Dr. Bekele M.',
    tatProgress: 55, tatLabel: '1h 45m', tatColor: 'amber',
    reportStatus: 'Draft', reportStatusColor: 'amber',
  },
  {
    id: 3, alert: false,
    priority: 'Normal', priorityColor: 'blue',
    patientName: 'Etasy Abebe', patientId: '273220',
    age: 52, sex: 'M', studyDate: '2026-03-12 09:15',
    modality: 'DX', bodyPart: 'CHEST', images: 1,
    priors: 1, refPhysician: 'Dr. Hailu S.',
    tatProgress: 20, tatLabel: '18h left', tatColor: 'green',
    reportStatus: 'No Report', reportStatusColor: 'gray',
  },
  {
    id: 4, alert: false,
    priority: 'Routine', priorityColor: 'green',
    patientName: 'Asqual Seyoum', patientId: 'PAT004711',
    age: 29, sex: 'F', studyDate: '2026-03-11 14:30',
    modality: 'CR', bodyPart: 'SPINE LUMBAR', images: 1,
    priors: 0, refPhysician: 'Dr. Worku A.',
    tatProgress: 10, tatLabel: '36h left', tatColor: 'green',
    reportStatus: 'Submitted', reportStatusColor: 'green',
  },
];

// ─── Design tokens (extracted from Figma exactly) ────────────────────────────
// Figma node 2:54 — exact values: backdrop-blur-[65px], bg rgba(255,255,255,0.04),
// border-2 solid rgba(61,61,60,0.66), rounded-[30px]
const ALERT_STYLES = {
  red:    { bg: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(65px)', border: '2px solid rgba(61,61,60,0.66)', borderRadius: '30px', dotColor: '#E24B4A', textColor: '#E8E9ED' },
  amber:  { bg: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(65px)', border: '2px solid rgba(61,61,60,0.66)', borderRadius: '30px', dotColor: '#F59E0B', textColor: '#E8E9ED' },
  blue:   { bg: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(65px)', border: '2px solid rgba(61,61,60,0.66)', borderRadius: '30px', dotColor: '#64B5F6', textColor: '#E8E9ED' },
  orange: { bg: 'rgba(249,115,22,0.06)',  backdropFilter: 'blur(65px)', border: '2px solid rgba(249,115,22,0.40)', borderRadius: '30px', dotColor: '#F97316', textColor: '#FED7AA' },
  purple: { bg: 'rgba(167,139,250,0.06)', backdropFilter: 'blur(65px)', border: '2px solid rgba(167,139,250,0.40)', borderRadius: '30px', dotColor: '#A78BFA', textColor: '#DDD6FE' },
};

const PRIORITY_STYLES = {
  red:   { background: '#2C2426', color: '#E57373', borderColor: '#3D302E' },
  amber: { background: '#2E2A22', color: '#FFB74D', borderColor: '#3D3830' },
  blue:  { background: '#232931', color: '#64B5F6', borderColor: '#303944' },
  green: { background: '#242D28', color: '#81C784', borderColor: '#313D37' },
};

const TAT_BAR_COLOR = { red: '#E24B4A', amber: '#BA7517', green: '#1D9E75' };
const TAT_TEXT_COLOR = { red: '#E24B4A', amber: '#BA7517', green: '#1D9E75' };
const STATUS_DOT_COLOR = { amber: '#BA7517', green: '#1D9E75', gray: '#6B7280' };
// ─── TAT & Priority helpers ───────────────────────────────────────────────────
const TAT_HOURS = { stat: 1, urgent: 2, normal: 24, routine: 48 };
const PRIORITY_MAP = {
  stat:    { label: 'STAT',    color: 'red'   },
  urgent:  { label: 'Urgent',  color: 'amber' },
  normal:  { label: 'Normal',  color: 'blue'  },
  routine: { label: 'Routine', color: 'green' },
};

// Compute TAT label + bar fill % from assignment timestamp and priority.
// Pure function — safe to call inside render on every tick.
const computeTAT = (assignedAt, priorityKey) => {
  if (!assignedAt) return { tatProgress: 0, tatLabel: 'N/A', tatColor: 'green' };
  const hours = TAT_HOURS[(priorityKey || 'normal').toLowerCase()] || 24;
  const assigned = new Date(assignedAt);
  const deadline = new Date(assigned.getTime() + hours * 3600000);
  const now      = new Date();
  const total    = deadline - assigned;
  const elapsed  = now - assigned;
  const pct      = Math.min(100, Math.round((elapsed / total) * 100));
  const remaining = deadline - now;
  let label;
  if (remaining <= 0) {
    label = 'Overdue';
  } else {
    const h = Math.floor(remaining / 3600000);
    const m = Math.floor((remaining % 3600000) / 60000);
    label = h > 0 ? `${h}h ${m}m left` : `${m}m left`;
  }
  const color = pct >= 100 ? 'red' : pct >= 75 ? 'amber' : 'green';
  return { tatProgress: pct, tatLabel: label, tatColor: color };
};

// ─── Sort helpers ─────────────────────────────────────────────────────────────
const PRIORITY_RANK = { stat: 1, urgent: 2, normal: 3, routine: 4 };
// Returns ms until TAT deadline; negative = overdue, MAX = no timestamp
const tatRemainingMs = (assignedAt, priorityKey) => {
  if (!assignedAt) return Number.MAX_SAFE_INTEGER;
  const hours = TAT_HOURS[(priorityKey || 'normal').toLowerCase()] || 24;
  return new Date(assignedAt).getTime() + hours * 3600000 - Date.now();
};
// Active worklist: priority rank asc, then TAT remaining asc (overdue floats to top)
const sortActiveWorklist = (arr) =>
  [...arr].sort((a, b) => {
    const ra = PRIORITY_RANK[a.priorityKey] || 5;
    const rb = PRIORITY_RANK[b.priorityKey] || 5;
    if (ra !== rb) return ra - rb;
    return tatRemainingMs(a.assignedAt, a.priorityKey) - tatRemainingMs(b.assignedAt, b.priorityKey);
  });
// ─── Custom dropdown — replaces native <select> to avoid OS blue highlight ────
const CustomSelect = ({ value, onChange, options, height = '32px', style = {}, openUp = false }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const selected = options.find(o => o.value === value) || options[0];
  const hasValue = value !== '' && value !== undefined && value !== null;
  const PRIMARY = '#2a5f5a';
  const PRIMARY_RGBA = (a) => `rgba(42,95,90,${a})`;
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', ...style }}>
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen(o => !o)}
        style={{
          height, padding: '0 30px 0 12px', position: 'relative',
          background: open
            ? `linear-gradient(135deg, ${PRIMARY_RGBA(0.25)}, ${PRIMARY_RGBA(0.10)})`
            : hasValue
              ? `linear-gradient(135deg, ${PRIMARY_RGBA(0.18)}, ${PRIMARY_RGBA(0.06)})`
              : '#25282e',
          border: open
            ? `1px solid ${PRIMARY}`
            : hasValue
              ? `1px solid ${PRIMARY_RGBA(0.6)}`
              : '1px solid #555860',
          borderRadius: '8px',
          boxShadow: open ? `0 0 0 2px ${PRIMARY_RGBA(0.25)}, inset 0 1px 0 rgba(255,255,255,0.04)` : 'none',
          fontSize: '14px',
          color: hasValue ? '#7ab8b3' : '#D1D5DB',
          fontWeight: hasValue ? 600 : 400,
          outline: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', minWidth: '130px',
          transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
        }}
      >
        {selected?.label}
        <ChevronDown
          size={12}
          style={{
            position: 'absolute', right: '9px', top: '50%',
            transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%) rotate(0deg)',
            color: open ? '#7ab8b3' : '#9CA3AF',
            transition: 'transform 0.2s, color 0.15s',
            pointerEvents: 'none',
          }}
        />
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: openUp ? 'auto' : 'calc(100% + 6px)',
          bottom: openUp ? 'calc(100% + 6px)' : 'auto',
          left: 0, zIndex: 9999,
          background: 'linear-gradient(160deg, #1e2127, #23262d)',
          border: `1px solid ${PRIMARY_RGBA(0.45)}`,
          borderRadius: '10px',
          overflow: 'hidden',
          minWidth: '100%',
          boxShadow: `0 12px 32px rgba(0,0,0,0.6), 0 0 0 1px ${PRIMARY_RGBA(0.12)}`,
        }}>
          {/* accent line — top when opening down, bottom when opening up */}
          {!openUp && <div style={{ height: '2px', background: `linear-gradient(90deg, ${PRIMARY}, transparent)`, borderRadius: '10px 10px 0 0' }} />}
          {options.map((opt, idx) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  padding: '9px 14px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isSelected ? '#7ab8b3' : '#C9D1DA',
                  background: isSelected ? PRIMARY_RGBA(0.18) : 'transparent',
                  borderBottom: idx < options.length - 1 ? '1px solid rgba(58,61,68,0.5)' : 'none',
                  transition: 'background 0.12s, color 0.12s',
                  fontWeight: isSelected ? 600 : 400,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = PRIMARY_RGBA(0.28);
                  e.currentTarget.style.color = '#FFFFFF';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = isSelected ? PRIMARY_RGBA(0.18) : 'transparent';
                  e.currentTarget.style.color = isSelected ? '#7ab8b3' : '#C9D1DA';
                }}
              >
                {/* active dot indicator */}
                <span style={{
                  width: '5px', height: '5px', borderRadius: '50%', flexShrink: 0,
                  background: isSelected ? PRIMARY : 'transparent',
                  boxShadow: isSelected ? `0 0 6px ${PRIMARY}` : 'none',
                }} />
                {opt.label}
              </div>
            );
          })}
          {openUp && <div style={{ height: '2px', background: `linear-gradient(90deg, ${PRIMARY}, transparent)`, borderRadius: '0 0 10px 10px' }} />}
        </div>
      )}
    </div>
  );
};

const WorklistPage = (props) => {
  const {
    history,
    currentProject = {},
    users = {},
    profile = {},
  } = props;
  const [loading, setLoading] = useState(true);
  // True only while the FIRST fetch is in flight with nothing to show yet.
  // The 30 s background poll passes silent:true (so `loading` stays false) and
  // by then allStudiesData is populated — the table is never blanked mid-session.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const firstLoad = loading && !hasLoadedOnce;
  const [allStudiesData, setAllStudiesData] = useState([]);  // all assigned studies — unfiltered
  const [studiesData, setStudiesData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5 });
  const [activeTab, setActiveTab] = useState('active');
  const [tatTick, setTatTick] = useState(0);               // increments every 60 s to refresh TAT
  const [appliedFilterSearch, setAppliedFilterSearch] = useState('');
  const [filterSearch, setFilterSearch]     = useState('');
  const [filterModality, setFilterModality] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus]     = useState('');
  const [reportStatusCache, setReportStatusCache] = useState({});
  const [reportStatusLoading, setReportStatusLoading] = useState({});
  const [submittedAtCache, setSubmittedAtCache] = useState({});
  const [alerts, setAlerts] = useState([]);
  const dismissedAlertIdsRef = useRef(new Set());
  // Stored notifications loaded from localStorage (unassigned / reassigned events)
  const [storedNotifications, setStoredNotifications] = useState([]);
  // { id, x, y, text } — drives the floating assignment-notes popover
  const [notePopover, setNotePopover] = useState(null);

  const dismissAlert = (id, isStored) => {
    dismissedAlertIdsRef.current.add(id);
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, visible: false } : a)));
    // Persist the dismiss for stored notifications so it survives page refresh
    if (isStored) {
      const userId = profile?.sub || profile?.id || profile?.user_id || '';
      if (userId) dismissNotification(userId, id);
      setStoredNotifications((prev) => prev.map((n) => n.id === id ? { ...n, dismissed: true } : n));
    }
  };

  const hoverTimerRef = useRef(null);
  const notePopoverRef = useRef(null);

  const openNotePopover = (iconEl, study) => {
    clearTimeout(hoverTimerRef.current);
    const rect = iconEl.getBoundingClientRect();
    const flipped = (window.innerHeight - rect.bottom) < 200;
    setNotePopover({
      id: study.id,
      x: rect.left + rect.width / 2,
      y: flipped ? rect.top - 10 : rect.bottom + 10,
      text: study.assignmentNotes,
      flipped,
    });
  };

  const handleNoteIconClick  = (e, study) => { e.stopPropagation(); openNotePopover(e.currentTarget, study); };
  const handleNoteIconEnter  = (e, study) => { openNotePopover(e.currentTarget, study); };
  const handleNoteIconLeave  = () => { hoverTimerRef.current = setTimeout(() => setNotePopover(null), 300); };
  const handlePopoverEnter   = () => { clearTimeout(hoverTimerRef.current); };
  const handlePopoverLeave   = () => { hoverTimerRef.current = setTimeout(() => setNotePopover(null), 200); };

  // Close on outside click (no overlay needed — overlay blocks hover events)
  useEffect(() => {
    if (!notePopover) return;
    const onMouseDown = (e) => {
      if (notePopoverRef.current && notePopoverRef.current.contains(e.target)) return;
      if (e.target.closest('.wl-note-btn')) return;
      setNotePopover(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [notePopover]);

  // Close on scroll so the popover doesn't float detached
  useEffect(() => {
    if (!notePopover) return;
    const close = () => setNotePopover(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [notePopover]);

  const normalizeReportStatus = (status) => {
    const value = (status || 'none').toLowerCase();
    if (value === 'none' || value === 'draft' || value === 'submitted') {
      return value;
    }
    return 'unknown';
  };

  // Open the study in the AfyaNX desktop workstation (Weasis). Same shared
  // implementation the admin All Studies list uses, so the token hand-off and
  // the "did it actually launch?" explanation cannot drift between the two.
  const handleOpenWorkstation = (study) =>
    openStudyInWorkstation(study, { getStudyDetail, actionRefreshToken });

  const normalizeStudyUID = (studyUID) => {
    if (!studyUID || typeof studyUID !== 'string') return '';
    if (studyUID.match(/^[a-f0-9-]{36}\./i)) {
      return studyUID.substring(37);
    }
    return studyUID;
  };

  const reportStatusLabels = {
    none: 'No Report',
    draft: 'Draft',
    submitted: 'Submitted',
    unknown: 'Unknown',
  };

  // Listen for report status updates from Clinical Viewer (cross-tab communication)
  useEffect(() => {
    const handleReportStatusUpdate = (event) => {
      if (event.key !== 'report-status-update' || !event.newValue) {
        return;
      }

      try {
        const payload = JSON.parse(event.newValue);
        const rawStudyInstanceUID = payload?.rawStudyInstanceUID || payload?.studyInstanceUID;
        const studyInstanceUID = normalizeStudyUID(payload?.studyInstanceUID || rawStudyInstanceUID);
        const status = normalizeReportStatus(payload?.status);
        if (!studyInstanceUID && !rawStudyInstanceUID) return;

        setReportStatusCache((prev) => ({
          ...prev,
          ...(studyInstanceUID ? { [studyInstanceUID]: status } : {}),
          ...(rawStudyInstanceUID ? { [rawStudyInstanceUID]: status } : {}),
        }));
      } catch (error) {
        console.error('Failed to parse report status update:', error);
      }
    };

    window.addEventListener('storage', handleReportStatusUpdate);
    return () => window.removeEventListener('storage', handleReportStatusUpdate);
  }, []);

  // ── Load persisted notifications (unassigned / reassigned) from localStorage ──
  // Runs once when profile is available (we need the userId to read the right bucket).
  const loadStoredNotifications = () => {
    const userId = profile?.sub || profile?.id || profile?.user_id || '';
    if (!userId) return;
    const notifs = readNotifications(userId);
    setStoredNotifications(notifs);
    // Pre-populate dismissedAlertIdsRef so already-dismissed ones are never shown again
    notifs.filter((n) => n.dismissed).forEach((n) => dismissedAlertIdsRef.current.add(n.id));
  };

  useEffect(() => {
    loadStoredNotifications();
    // eslint-disable-next-line
  }, [profile?.sub, profile?.id, profile?.user_id]);

  // Listen for NEW notifications written by admin dialogs in the same browser session
  useEffect(() => {
    const handleNotifUpdate = (event) => {
      if (event.key !== NOTIFICATION_UPDATED_KEY || !event.newValue) return;
      try {
        const { userId } = JSON.parse(event.newValue);
        const currentUserId = profile?.sub || profile?.id || profile?.user_id || '';
        if (userId === currentUserId) loadStoredNotifications();
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('storage', handleNotifUpdate);
    return () => window.removeEventListener('storage', handleNotifUpdate);
    // eslint-disable-next-line
  }, [profile]);

  useEffect(() => {
    const interval = setInterval(() => setTatTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // Derive the displayed table rows whenever source data, tab, or filters change
  useEffect(() => {
    let data = allStudiesData;

    // Tab: Active = not submitted; Completed = submitted
    if (activeTab === 'active') {
      data = data.filter((s) => (reportStatusCache[s.studyInstanceUID] || 'none') !== 'submitted');
    } else {
      data = data.filter((s) => (reportStatusCache[s.studyInstanceUID] || 'none') === 'submitted');
    }

    // Text search (applied only after Search button is clicked)
    if (appliedFilterSearch) {
      const q = appliedFilterSearch.toLowerCase();
      data = data.filter(
        (s) =>
          (s.patientName    || '').toLowerCase().includes(q) ||
          (s.patientId      || '').toLowerCase().includes(q) ||
          (s.accessionNumber|| '').toLowerCase().includes(q),
      );
    }

    // Modality filter
    if (filterModality) {
      data = data.filter((s) => s.modality === filterModality);
    }

    // Priority filter (dropdown value is title-case e.g. 'STAT'; priorityKey is lowercase)
    if (filterPriority) {
      data = data.filter((s) => s.priorityKey === filterPriority.toLowerCase());
    }

    // Status filter
    if (filterStatus) {
      const statusMap = { 'No Report': 'none', Draft: 'draft', Submitted: 'submitted' };
      const targetStatus = statusMap[filterStatus];
      if (targetStatus) {
        data = data.filter((s) => (reportStatusCache[s.studyInstanceUID] || 'none') === targetStatus);
      }
    }

    // Sort: active = priority rank + TAT remaining asc; completed = most recently assigned first
    if (activeTab === 'active') {
      data = sortActiveWorklist(data);
    } else {
      data = [...data].sort((a, b) => {
        const ta = a.assignedAt ? new Date(a.assignedAt).getTime() : 0;
        const tb = b.assignedAt ? new Date(b.assignedAt).getTime() : 0;
        return tb - ta;
      });
    }

    const { current, pageSize } = pagination;
    setTotalCount(data.length);
    setStudiesData(data.slice((current - 1) * pageSize, current * pageSize));
    // eslint-disable-next-line
  }, [allStudiesData, reportStatusCache, activeTab, appliedFilterSearch, filterModality, filterPriority, filterStatus, pagination.current, pagination.pageSize, tatTick]);

  const getCurrentUserId = () =>
    profile?.sub || profile?.id || profile?.user_id || '';

  const getUserRoles = () => profile?.realm_roles || [];

  const isAdminUser = () => {
    const roles = getUserRoles();
    const adminRoles = ['PROJECT_OWNER', 'MANAGER', 'ADMIN', 'Administrator'];
    return roles.some((role) => adminRoles.includes(role));
  };

  const calculateAge = (birthDate) => {
    if (!birthDate || birthDate.length < 8) return 'N/A';
    try {
      const birthDateObj = moment(birthDate.substring(0, 8), 'YYYYMMDD');
      return moment().diff(birthDateObj, 'years');
    } catch {
      return 'N/A';
    }
  };

  // Monotonic id for in-flight fetches. The initial load and the 30 s poll can
  // overlap (and a slow response can land after a fast one), so a response is
  // only allowed to touch state if it is still the newest request. Without this
  // an older reply could overwrite the list with stale rows — another way the
  // worklist appeared to change under the user.
  const fetchGenerationRef = useRef(0);

  const fetchStudies = async (params = {}) => {
    const generation = ++fetchGenerationRef.current;
    const isCurrent = () => fetchGenerationRef.current === generation;
    if (!params.silent) setLoading(true);
    try {
      console.log('[Worklist] Fetching studies for radiologist via secure endpoint');

      // Call secure backend endpoint that filters by authenticated user.
      // This is ONE round-trip and is what the user actually waits on.
      const { data } = await getRadiologistWorklist();

      // Parse Orthanc study format
      const formattedData = (data?.data || []).map((study, index) => {
        const mainTags = study.MainDicomTags || {};
        const patientTags = study.PatientMainDicomTags || {};
        const seriesTags = study.SeriesData?.MainDicomTags || {};

        // Assignment data enriched on the backend
        const assignment = study._assignment || {};
        const priorityKey      = (assignment.priority || 'normal').toLowerCase();
        const priorityInfo     = PRIORITY_MAP[priorityKey] || PRIORITY_MAP.normal;
        const assignedAt       = assignment.assigned_at || null;
        const assignmentNotes  = (assignment.notes || '').trim();
        const priorsCount  = typeof study._priors_count === 'number' ? study._priors_count : 0;

        const modality = getPrimaryModality(seriesTags.Modality, mainTags.ModalitiesInStudy);
        const bodyPart = seriesTags.BodyPartExamined || mainTags.BodyPartExamined || 'N/A';
        const seriesDescription = seriesTags.SeriesDescription || mainTags.StudyDescription || '';

        // Preserve raw StudyInstanceUID and derive clean UID for display/cache keys
        const rawStudyUID = mainTags.StudyInstanceUID || '';
        let studyUID = rawStudyUID;
        if (studyUID.includes('.') && studyUID.match(/^[a-f0-9-]{36}\./i)) {
          studyUID = studyUID.substring(37);
        }

        const birthDate = patientTags.PatientBirthDate || '';
        const ageVal = calculateAge(birthDate);
        const sexRaw = (patientTags.PatientSex || 'U').toUpperCase();
        const sexLabel = sexRaw === 'M' ? 'M' : sexRaw === 'F' ? 'F' : 'U';
        const seriesCount = parseInt(mainTags.NumberOfStudyRelatedSeries, 10)
          || parseInt(study.CountSeries, 10)
          || (Array.isArray(study.Series) ? study.Series.length : 0)
          || 0;
        const imagesCount = parseInt(mainTags.NumberOfStudyRelatedInstances, 10)
          || parseInt(study.CountInstances, 10)
          || 0;

        return {
          key: study.ID || index,
          id: study.ID,
          patientName: patientTags.PatientName || 'N/A',
          patientID: patientTags.PatientID || 'N/A',
          patientId: patientTags.PatientID || 'N/A',   // alias used by table cells
          accessionNumber: mainTags.AccessionNumber || '',
          age: ageVal,
          sex: sexLabel,
          seriesCount,
          imagesCount,
          priors: priorsCount,
          refPhysician: mainTags.ReferringPhysicianName || 'N/A',
          priority: priorityInfo.label,
          priorityColor: priorityInfo.color,
          priorityKey,                                 // lowercase key for filtering & TAT
          assignedAt,                                  // ISO string from assignment record
          assignmentNotes,                             // optional free-text from admin
          alert: false,
          bodyPart: bodyPart,
          modality: modality,
          studyDate: mainTags.StudyDate && mainTags.StudyTime
            ? moment(mainTags.StudyDate + mainTags.StudyTime.substring(0, 6), 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm')
            : mainTags.StudyDate
            ? moment(mainTags.StudyDate, 'YYYYMMDD').format('YYYY-MM-DD')
            : 'N/A',
          studyDateRaw: mainTags.StudyDate || '',
          studyInstanceUID: studyUID,
          rawStudyInstanceUID: rawStudyUID,
          studyDescription: seriesDescription,
        };
      });

      console.log('[Worklist] Received', formattedData.length, 'studies from secure backend');

      // ── Resolve every report status in ONE request, then commit once ───
      // The worklist tabs are defined BY report status (Active = not
      // submitted), so a row whose status is unknown is a row that cannot be
      // placed yet. The previous version rendered all studies immediately and
      // then fired one request PER STUDY to fill statuses in afterwards. On
      // React 17 those replies are not batched, so each arriving status caused
      // its own re-render and any submitted study vanished from Active one row
      // at a time — the list visibly loaded and then shrank. With ~30 studies
      // the browser's 6-connection cap also serialised them into several
      // round-trips, which is why it sometimes needed a manual refresh.
      //
      // One bulk call removes both problems: the server fans out over its fast
      // link to Orthanc, and the table is painted once, already correct.
      const statusMap = {};
      const submittedMap = {};
      try {
        const queryUIDs = formattedData
          .map((s) => s.rawStudyInstanceUID || s.studyInstanceUID)
          .filter(Boolean);
        const entries = queryUIDs.length
          ? (await getBulkReportStatus(queryUIDs))?.data?.data || {}
          : {};
        const currentUserId = getCurrentUserId();
        formattedData.forEach((study) => {
          const cacheUID = study.studyInstanceUID;
          const queryUID = study.rawStudyInstanceUID || study.studyInstanceUID;
          const entry = entries[queryUID] || entries[cacheUID] || {};
          // If the report was signed by a DIFFERENT radiologist (study
          // force-reassigned after submission), the current assignee has not
          // written their own report yet, so it belongs in Active. Unchanged
          // rule — signer_sub shares the key space with the Keycloak sub.
          const signerSub = entry.signer_sub || '';
          const effectiveStatus =
            signerSub && currentUserId && signerSub !== currentUserId
              ? 'none'
              : normalizeReportStatus(entry.status);

          statusMap[cacheUID] = effectiveStatus;
          if (queryUID && queryUID !== cacheUID) statusMap[queryUID] = effectiveStatus;

          if (entry.timestamp && effectiveStatus === 'submitted') {
            submittedMap[cacheUID] = entry.timestamp;
            if (queryUID && queryUID !== cacheUID) submittedMap[queryUID] = entry.timestamp;
          }
        });
      } catch (statusError) {
        // Non-fatal: show the worklist anyway. Unknown statuses fall back to
        // 'none', which puts every study on Active — the safe default, because
        // a radiologist seeing an extra row beats a missing one.
        console.error('[Worklist] Bulk report status failed; showing studies unfiltered:', statusError);
      }

      // A newer fetch started while this one was in flight — drop this result.
      if (!isCurrent()) return;

      // React 17 does not batch state updates inside async callbacks, so these
      // would otherwise commit as separate renders and the middle one would
      // show the new rows against the old statuses. Batch them so the table
      // goes straight from its previous state to the correct new one.
      unstable_batchedUpdates(() => {
        setReportStatusCache((prev) => ({ ...prev, ...statusMap }));
        setSubmittedAtCache((prev) => ({ ...prev, ...submittedMap }));
        setAllStudiesData(formattedData);
        setHasLoadedOnce(true);
        if (!params.silent) setLoading(false);
      });

    } catch (error) {
      console.error('Failed to fetch studies:', error);
      if (!params.silent) {
        // api.js interceptor rejects with the raw response object — read status from both shapes.
        const httpStatus = error?.response?.status ?? error?.status;
        const responseData = error?.response?.data ?? error?.data ?? {};
        const errCode = responseData.error || '';
        const errMsg = responseData.message || '';
        const isTenantError = httpStatus === 403 && (
          errCode === 'TENANT_NOT_PROVISIONED' ||
          errCode.includes('not a known tenant') ||
          errCode.includes('hospital_id') ||
          errMsg.includes('not a known tenant') ||
          errMsg.includes('hospital_id')
        );
        if (isTenantError) {
          message.warning({
            content: 'This hospital has not been provisioned in the PACS system yet. Please contact your platform administrator to complete the setup.',
            duration: 0,
            key: 'tenant-not-provisioned',
          });
        } else {
          message.error('Failed to fetch studies');
        }
      }
      setHasLoadedOnce(true);
      if (!params.silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudies();
    props.actionGetUsers();
    // eslint-disable-next-line
  }, []);

  // Auto-refresh every 30 s — silent background poll, no loading spinner
  useEffect(() => {
    const interval = setInterval(() => { fetchStudies({ silent: true }); }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination(newPagination);
  };

  const handleSearch = () => {
    setAppliedFilterSearch(filterSearch);
    setPagination(p => ({ current: 1, pageSize: p.pageSize }));
  };

  const handleClear = () => {
    setFilterSearch('');
    setFilterModality('');
    setFilterPriority('');
    setFilterStatus('');
    setAppliedFilterSearch('');
    setPagination(p => ({ current: 1, pageSize: p.pageSize }));
  };

  const handleOpenViewer = async (record) => {
    try {
      console.log('[WORKLIST VIEWER] Opening study:', { studyId: record.id, studyInstanceUID: record.studyInstanceUID });

      if (!record.id) {
        message.error('Study ID not found');
        return;
      }

      let resolvedUID = record.studyInstanceUID;
      let studyDetail = null;
      
      // Fetch study detail from Orthanc to get correct UID format and validate series
      try {
        console.log('[WORKLIST VIEWER] Fetching study detail from Orthanc ID:', record.id);
        const { data } = await getStudyDetail(record.id);
        studyDetail = data;
        console.log('[WORKLIST VIEWER] Study detail fetched:', { 
          hasSeries: data?.Series?.length > 0, 
          seriesCount: data?.Series?.length,
          studyUID: data?.MainDicomTags?.StudyInstanceUID
        });
        
        // Normalize the UID: strip VinLab project UUID prefix (project_id.original_uid).
        // Project studies have a UUID-prefixed UID in Orthanc which QIDO-RS cannot match
        // because hyphens are not valid in DICOM UIDs. Use record.studyInstanceUID as
        // fallback — it is already normalized in fetchStudies.
        const rawUID = data?.MainDicomTags?.StudyInstanceUID || record.studyInstanceUID;
        resolvedUID = normalizeStudyUID(rawUID) || record.studyInstanceUID;
      } catch (error) {
        console.error('[WORKLIST VIEWER] Failed to fetch study details:', error);
        message.error('Failed to fetch study details from PACS');
        return;
      }

      if (!resolvedUID) {
        console.error('[WORKLIST VIEWER] No StudyInstanceUID found');
        message.error('StudyInstanceUID not found for the selected study');
        return;
      }

      // Validate that study has series data
      if (studyDetail) {
        const hasSeries = studyDetail.Series && Array.isArray(studyDetail.Series) && studyDetail.Series.length > 0;
        console.log('[WORKLIST VIEWER] Series validation:', { hasSeries, seriesCount: studyDetail.Series?.length });
        
        if (!hasSeries) {
          console.error('[WORKLIST VIEWER] BLOCKED - Study has no series data');
          message.error('This study has incomplete DICOM data and cannot be opened. The study needs to be re-uploaded with proper DICOM files.');
          return;
        }
      } else {
        console.error('[WORKLIST VIEWER] BLOCKED - No study detail available');
        message.error('Unable to validate study data. Please try again.');
        return;
      }

      console.log('[WORKLIST VIEWER] PASSED - Opening viewer with StudyInstanceUID:', resolvedUID);

      const listStudyUID = [
        {
          type: SESSION_TYPE.STUDY,
          id: resolvedUID,
          meta: {
            study_code: record.patientID,
            study_id: record.id,
            project_name: 'Clinical Studies',
          },
        },
      ];

      const { data } = await actionCreateSession({ data: listStudyUID });
      const { session_id } = data?.data || {};
      
      if (session_id) {
        // Open in AfyaNX Viewer (OHIF v3) — clinical-viewer removed
        const rawUID = record?.studyInstanceUID || '';
        if (rawUID) {
          window.open('/afya-viewer/viewer?StudyInstanceUIDs=' + encodeURIComponent(rawUID), '_blank');
        } else {
          message.error('StudyInstanceUID not found');
        }
      } else {
        message.error('Failed to create viewer session');
      }
    } catch (error) {
      console.error('Error opening viewer:', error);
      message.error('Failed to open viewer');
    }
  };

  const handleOpenV3 = async (record, mode = 'viewer') => {
    if (!record?.id) {
      message.error('Study ID not found');
      return;
    }
    try {
      const { data } = await getStudyDetail(record.id);
      const rawUID = data?.MainDicomTags?.StudyInstanceUID || record.studyInstanceUID;
      if (!rawUID) {
        message.error('StudyInstanceUID not found');
        return;
      }
      const hasSeries = data?.Series && Array.isArray(data.Series) && data.Series.length > 0;
      if (!hasSeries) {
        message.error('This study has incomplete DICOM data and cannot be opened.');
        return;
      }
      // VinLab project studies have a UUID prefix (project_id.original_uid) in Orthanc.
      // Orthanc's QIDO-RS cannot match non-standard UIDs with hyphens, so strip the prefix.
      // record.studyInstanceUID is already normalized in fetchStudies as a reliable fallback.
      const resolvedUID = normalizeStudyUID(rawUID) || record.studyInstanceUID;
      window.open('/afya-viewer/' + mode + '?StudyInstanceUIDs=' + encodeURIComponent(resolvedUID), '_blank');
    } catch (error) {
      console.error('Error opening OHIF v3 viewer:', error);
      message.error('Failed to open OHIF v3 viewer');
    }
  };

  // ─── Stat card counts (recompute on data + report-status + TAT tick) ────────
  const statCounts = useMemo(() => {
    void tatTick; // ensure re-evaluation every 60 s for the TAT-overdue card
    const statusFor = (s) => reportStatusCache[s.studyInstanceUID] || 'none';
    const c1 = allStudiesData.length;
    const c2 = allStudiesData.filter(
      (s) => ['stat', 'urgent'].includes(s.priorityKey) && statusFor(s) !== 'submitted',
    ).length;
    const c3 = allStudiesData.filter(
      (s) => computeTAT(s.assignedAt, s.priorityKey).tatColor === 'red' && statusFor(s) !== 'submitted',
    ).length;
    const c4 = allStudiesData.filter((s) => statusFor(s) === 'draft').length;
    const c5 = allStudiesData.filter((s) => statusFor(s) === 'submitted').length;
    return [c1, c2, c3, c4, c5];
    // eslint-disable-next-line
  }, [allStudiesData, reportStatusCache, tatTick]);

  // ─── Alert strip real conditions ──────────────────────────────────────────
  useEffect(() => {
    if (allStudiesData.length === 0 && storedNotifications.filter(n => !n.dismissed).length === 0) return;
    const now = new Date();
    const statusFor = (s) => reportStatusCache[s.studyInstanceUID] || 'none';

    // Alert 1 (red): STAT/Urgent studies that hit their TAT deadline and are still unread
    const statOverdue = allStudiesData.filter(
      (s) =>
        ['stat', 'urgent'].includes(s.priorityKey) &&
        statusFor(s) !== 'submitted' &&
        computeTAT(s.assignedAt, s.priorityKey).tatColor === 'red',
    );

    // Alert 2 (amber): draft reports awaiting submission
    const draftCount = allStudiesData.filter((s) => statusFor(s) === 'draft').length;

    // Alert 3 (blue): most-recent study assigned in the last 60 minutes
    const recentlyAssigned = allStudiesData
      .filter((s) => s.assignedAt && now - new Date(s.assignedAt) <= 60 * 60000)
      .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));

    const newAlerts = [];
    if (statOverdue.length > 0 && !dismissedAlertIdsRef.current.has(1)) {
      newAlerts.push({
        id: 1, type: 'red', visible: true,
        text: `${statOverdue.length} STAT ${statOverdue.length === 1 ? 'study' : 'studies'} unread — TAT deadline exceeded`,
      });
    }
    if (draftCount > 0 && !dismissedAlertIdsRef.current.has(2)) {
      newAlerts.push({
        id: 2, type: 'amber', visible: true,
        text: `${draftCount} draft ${draftCount === 1 ? 'report' : 'reports'} awaiting your signature`,
      });
    }
    if (recentlyAssigned.length > 0) {
      const s = recentlyAssigned[0];
      // Use a per-study unique ID so dismissing one new-assignment alert
      // does not suppress future alerts for different assignments.
      const newAssignAlertId = `assigned-${s.studyInstanceUID}-${s.assignedAt}`;
      if (!dismissedAlertIdsRef.current.has(newAssignAlertId)) {
        newAlerts.push({
          id: newAssignAlertId, type: 'blue', visible: true,
          text: `New study assigned: ${s.patientName} — ${s.bodyPart} ${s.modality}`,
        });
      }
    }

    // Alert 4 (orange): study was unassigned from this radiologist by admin
    // Alert 5 (purple): study was reassigned to another radiologist by admin
    // These come from the persistent notificationStore (survive page reloads, last 48 h).
    storedNotifications
      .filter((n) => !n.dismissed && !dismissedAlertIdsRef.current.has(n.id))
      .forEach((n) => {
        const studyLabel = [n.patientName, n.bodyPart, n.modality].filter(Boolean).join(' — ');
        if (n.type === 'unassigned') {
          newAlerts.push({
            id: n.id, type: 'orange', visible: true, isStored: true,
            text: `Study removed from your worklist: ${studyLabel}`,
          });
        } else if (n.type === 'reassigned') {
          newAlerts.push({
            id: n.id, type: 'purple', visible: true, isStored: true,
            text: `Study reassigned to another radiologist: ${studyLabel}`,
          });
        } else if (n.type === 'inherited') {
          newAlerts.push({
            id: n.id, type: 'purple', visible: true, isStored: true,
            text: `Study with prior report assigned to you: ${studyLabel}`,
          });
        }
      });

    setAlerts(newAlerts);
    // eslint-disable-next-line
  }, [allStudiesData, reportStatusCache, storedNotifications]);

  // ─── NEW FIGMA UI ────────────────────────────────────────────────────────────

  return (
    <>
    <div className="common-style-page worklist-redesign" style={{ padding: '20px 24px' }}>
      {/* ── 1. Alert Strip ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {alerts.filter((a) => a.visible).map((alert) => {
            const s = ALERT_STYLES[alert.type] || ALERT_STYLES.blue;
            return (
              <div
                key={alert.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  height: '40px', padding: '0 16px',
                  borderRadius: s.borderRadius,
                  background: s.bg,
                  backdropFilter: s.backdropFilter,
                  WebkitBackdropFilter: s.backdropFilter,
                  border: s.border,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: s.dotColor, flexShrink: 0 }} />
                  <span style={{ fontSize: '13px', fontWeight: 500, color: s.textColor }}>{alert.text}</span>
                </div>
                <button
                  onClick={() => dismissAlert(alert.id, alert.isStored)}
                  className="wl-dismiss-btn"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: s.textColor, opacity: 0.5, lineHeight: 1 }}
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 2. Stats Cards ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
          {[
            { label: 'Assigned to me',  value: statCounts[0], sub: 'Total studies',       Icon: UserCircle   },
            { label: 'STAT / Urgent',   value: statCounts[1], sub: 'Need immediate read', Icon: AlertTriangle },
            { label: 'TAT Overdue',     value: statCounts[2], sub: 'Deadline passed',     Icon: Clock        },
            { label: 'Drafts Pending',  value: statCounts[3], sub: 'Awaiting signature',  Icon: FileText     },
            { label: 'Completed Today', value: statCounts[4], sub: 'Submitted reports',   Icon: CheckCircle  },
          ].map(({ label, value, sub, Icon }) => (
            <div key={label} style={{
              padding: '16px',
              borderRadius: '20px',
              position: 'relative',
              overflow: 'hidden',
              background: 'rgba(42,95,90,0.05)',
              backdropFilter: 'blur(185.914px)',
              WebkitBackdropFilter: 'blur(185.914px)',
              border: '1px solid rgba(42,95,90,0.66)'
            }}>
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(42,95,90,0.20)', flexShrink: 0 }}>
                    <Icon size={16} style={{ color: '#7ecdc4', display: 'block' }} />
                  </div>
                  <span style={{ fontSize: '12px', color: '#C9D1DA', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
                </div>
                <p style={{ margin: '0 0 2px', fontSize: '30px', fontWeight: 700, color: firstLoad ? '#4b5563' : '#FFFFFF', lineHeight: 1.1 }}>{firstLoad ? '—' : value}</p>
                <span style={{ fontSize: '12px', color: '#C9D1DA' }}>{sub}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 2b. Active / Completed Tabs ──────────────────────────────────── */}
      <div style={{ display: 'flex', borderBottom: '1px solid #3a3d44', marginBottom: '16px' }}>
        {['active', 'completed'].map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setPagination(p => ({ current: 1, pageSize: p.pageSize })); }}
            style={{
              padding: '8px 20px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #1D9E75' : '2px solid transparent',
              marginBottom: '-1px',
              color: activeTab === tab ? '#1D9E75' : '#9CA3AF',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab ? 600 : 400,
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ── 3. Filter Bar ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6B7280', pointerEvents: 'none' }} />
          <input
            className="wl-search-input"
            placeholder="Search patient, ID or accession..."
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setAppliedFilterSearch(filterSearch); setPagination(p => ({ current: 1, pageSize: p.pageSize })); } }}
            style={{
              width: '100%', height: '32px', paddingLeft: '36px', paddingRight: '12px',
              background: '#25282e', border: '1px solid #555860', borderRadius: '6px',
              fontSize: '14px', color: '#D1D5DB', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        {[
          { val: filterModality, set: setFilterModality, opts: ['All modalities','DX','CR','CT','MR','US'] },
          { val: filterPriority, set: setFilterPriority, opts: ['All priorities','STAT','Urgent','Normal','Routine'] },
          { val: filterStatus,   set: setFilterStatus,   opts: ['All statuses','No Report','Draft','Submitted','Completed'] },
        ].map((sel, i) => (
          <CustomSelect
            key={i}
            value={sel.val}
            onChange={(v) => { sel.set(v); setPagination(p => ({ current: 1, pageSize: p.pageSize })); }}
            options={sel.opts.map((o, oi) => ({ value: oi === 0 ? '' : o, label: o }))}
          />
        ))}
        <button
          className="wl-btn-search"
          onClick={handleSearch}
          style={{
            height: '32px', padding: '0 16px',
            background: '#2a5f5a', border: 'none', borderRadius: '6px',
            fontSize: '14px', color: '#FFFFFF', cursor: 'pointer', fontWeight: 500,
          }}
        >Search</button>
        <button
          className="wl-btn-clear"
          onClick={handleClear}
          style={{
            height: '32px', padding: '0 16px',
            background: 'transparent', border: '1px solid #555860', borderRadius: '6px',
            fontSize: '14px', color: '#FFFFFF', cursor: 'pointer',
          }}
        >Clear Filter</button>
      </div>

      {/* ── Priority Legend (no container — inline reference row) ────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0', marginBottom: '5px', paddingLeft: '2px' }}>
        {[
          { key: 'red',   label: 'STAT',    desc: 'Emergency' },
          { key: 'amber', label: 'Urgent',  desc: 'High — 2h' },
          { key: 'blue',  label: 'Normal',  desc: 'Std — 24h' },
          { key: 'green', label: 'Routine', desc: 'Low — 48h' },
        ].map((item, i) => {
          const ps = PRIORITY_STYLES[item.key];
          return (
            <React.Fragment key={item.key}>
              {i > 0 && <span style={{ color: '#3a3d44', margin: '0 8px', fontSize: '10px', userSelect: 'none' }}>|</span>}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <span style={{
                  display: 'inline-flex', padding: '1px 5px', borderRadius: '3px',
                  fontSize: '10px', fontWeight: 600,
                  background: ps.background, color: ps.color,
                  border: `1px solid ${ps.borderColor}`,
                }}>{item.label}</span>
                <span style={{ fontSize: '11px', color: '#6B7280', whiteSpace: 'nowrap' }}>{item.desc}</span>
              </span>
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Hint text ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '10px', paddingLeft: '2px' }}>
        <span style={{ fontSize: '11px', color: '#9CA3AF', userSelect: 'none' }}>ⓘ</span>
        <span style={{ fontSize: '11px', color: '#D1D5DB', fontStyle: 'italic' }}>
          Double-click any row or click <span style={{ fontWeight: 600, fontStyle: 'normal', color: '#FFFFFF' }}>Read</span> to open a study in the viewer, or the monitor icon to open it in the AfyaNX Workstation
        </span>
      </div>

      {/* ── 4. Worklist Table ──────────────────────────────────────────────── */}
      <div style={{
        border: '1px solid #3a3d44', borderRadius: '12px', overflow: 'hidden',
        background: 'linear-gradient(to bottom right, #2d3038, #25282e, #1e2127)',
      }}>
        {/* overflow:auto for horizontal scroll; maxHeight caps at 5 rows (44px each) + header (~44px) */}
        <div style={{ overflow: 'auto', maxHeight: '264px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: '#23262d', borderBottom: '1px solid #3a3d44' }}>
                {['', 'Priority', 'Patient Name', 'Patient ID', 'Age/Sex', 'Study Date', 'Mod.', 'Body Part', 'Series/Images', 'Priors', 'Physician', 'TAT', 'Status', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: '12px 10px', textAlign: 'left', whiteSpace: 'nowrap', background: '#23262d' }}>
                    {h && <span style={{ fontSize: '12px', fontWeight: 600, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.02em' }}>{h}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {firstLoad && (
                <tr>
                  <td colSpan={12} style={{ padding: '48px 10px', textAlign: 'center', color: '#9aa4b2', fontSize: '14px' }}>
                    <Spin size="small" />
                    <span style={{ marginLeft: '10px' }}>Loading your worklist…</span>
                  </td>
                </tr>
              )}
              {!firstLoad && studiesData.length === 0 && (
                <tr>
                  <td colSpan={12} style={{ padding: '48px 10px', textAlign: 'center', color: '#9aa4b2', fontSize: '14px' }}>
                    No studies in this list.
                  </td>
                </tr>
              )}
              {!firstLoad && studiesData.map((study) => {
                void tatTick; // re-evaluate TAT display on every 60-second tick
                const pStyle = PRIORITY_STYLES[study.priorityColor] || PRIORITY_STYLES.blue;
                const { tatProgress, tatLabel, tatColor } = computeTAT(study.assignedAt, study.priorityKey);
                const tatBarColor  = TAT_BAR_COLOR[tatColor]  || '#1D9E75';
                const tatTextColor = TAT_TEXT_COLOR[tatColor] || '#1D9E75';
                // Derive report status live from async cache
                const rawCacheStatus = reportStatusCache[study.studyInstanceUID];
                const isStatusLoading = reportStatusLoading[study.studyInstanceUID];
                const reportStatusLabel = rawCacheStatus
                  ? ({ none: 'No Report', draft: 'Draft', submitted: 'Submitted', unknown: 'Unknown' }[rawCacheStatus] || rawCacheStatus)
                  : (isStatusLoading ? '…' : 'No Report');
                const reportStatusColorKey = rawCacheStatus === 'draft' ? 'amber'
                  : rawCacheStatus === 'submitted' ? 'green' : 'gray';
                const dotColor = STATUS_DOT_COLOR[reportStatusColorKey] || '#6B7280';
                return (
                  <tr
                    key={study.id}
                    className={`wl-table-row${study.priorityColor === 'red' ? ' wl-row-stat' : ''}`}
                    style={{ height: '44px', borderBottom: '1px solid #3a3d44', cursor: 'pointer' }}
                    onDoubleClick={() => handleOpenViewer(study)}
                  >
                    {/* Alert indicator — only STAT gets the "!" marker */}
                    <td style={{ padding: '0 8px', whiteSpace: 'nowrap', width: '24px', textAlign: 'center' }}>
                      {study.priorityKey === 'stat' && (
                        <span style={{ color: '#E24B4A', fontWeight: 700, fontSize: '15px', letterSpacing: '-0.5px' }}>!</span>
                      )}
                    </td>
                    {/* Priority badge + optional notes icon */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{
                          display: 'inline-flex', padding: '4px 8px', borderRadius: '4px',
                          fontSize: '12px', fontWeight: 600,
                          background: pStyle.background, color: pStyle.color,
                          border: `1px solid ${pStyle.borderColor}`,
                        }}>{study.priority}</span>
                        {study.assignmentNotes && (
                          <button
                            className="wl-note-btn"
                            onClick={(e) => handleNoteIconClick(e, study)}
                            onMouseEnter={(e) => handleNoteIconEnter(e, study)}
                            onMouseLeave={handleNoteIconLeave}
                            style={{
                              background: 'none', border: 'none', padding: '2px 3px',
                              cursor: 'pointer', borderRadius: '4px', lineHeight: 0,
                              color: notePopover?.id === study.id ? '#1D9E75' : '#3da295',
                            }}
                          >
                            <MessageSquare size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                    {/* Patient Name */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: '#FFFFFF' }}>{study.patientName}</span>
                    </td>
                    {/* Patient ID */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '14px', color: '#D8DCE8' }}>{study.patientId}</span>
                    </td>
                    {/* Age / Sex */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '14px', color: '#D8DCE8' }}>{study.age}&nbsp;/&nbsp;{study.sex}</span>
                    </td>
                    {/* Study Date */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap', minWidth: '120px' }}>
                      <span style={{ fontSize: '14px', color: '#D8DCE8' }}>{study.studyDate}</span>
                    </td>
                    {/* Modality chip */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <span style={{
                        display: 'inline-flex', padding: '2px 8px', borderRadius: '4px',
                        border: '1px solid #374151', fontSize: '12px', color: '#9CA3AF',
                      }}>{study.modality}</span>
                    </td>
                    {/* Body Part */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '14px', color: '#D8DCE8' }}>{study.bodyPart}</span>
                    </td>
                    {/* Series / Images */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '13px', color: '#D8DCE8' }}>
                        {study.seriesCount > 0 ? `${study.seriesCount}s` : '—'}
                        {study.imagesCount > 0 ? ` / ${study.imagesCount}` : ''}
                      </span>
                    </td>
                    {/* Priors */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      {study.priors > 0 ? (
                        <span className="wl-priors-link" style={{ fontSize: '14px', color: '#1D9E75', cursor: 'pointer' }}>
                          {study.priors}&nbsp;{study.priors === 1 ? 'prior' : 'priors'}
                        </span>
                      ) : (
                        <span style={{ fontSize: '14px', color: '#6B7280' }}>No priors</span>
                      )}
                    </td>
                    {/* Physician */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '14px', color: '#D8DCE8' }}>{study.refPhysician || 'N/A'}</span>
                    </td>
                    {/* TAT */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      {rawCacheStatus === 'submitted' ? (() => {
                        const submittedAt = submittedAtCache[study.studyInstanceUID];
                        if (!submittedAt || !study.assignedAt) {
                          return <span style={{ fontSize: '12px', fontWeight: 600, color: '#1D9E75' }}>✓ Done</span>;
                        }
                        const takenMs = new Date(submittedAt).getTime() - new Date(study.assignedAt).getTime();
                        const hours = TAT_HOURS[(study.priorityKey || 'normal').toLowerCase()] || 24;
                        const deadlineMs = hours * 3600000;
                        const isLate = takenMs > deadlineMs;
                        const absTakenMs = Math.abs(takenMs);
                        const th = Math.floor(absTakenMs / 3600000);
                        const tm = Math.floor((absTakenMs % 3600000) / 60000);
                        const takenLabel = th > 0 ? `${th}h ${tm}m` : `${tm}m`;
                        return isLate
                          ? <span style={{ fontSize: '12px', fontWeight: 600, color: '#BA7517', whiteSpace: 'nowrap' }}>{takenLabel} late</span>
                          : <span style={{ fontSize: '12px', fontWeight: 600, color: '#1D9E75', whiteSpace: 'nowrap' }}>✓ {takenLabel}</span>;
                      })() : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '48px', height: '6px', background: '#1F2937', borderRadius: '9999px', overflow: 'hidden', flexShrink: 0 }}>
                            <div style={{ height: '100%', width: `${tatProgress}%`, background: tatBarColor, borderRadius: '9999px' }} />
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: tatTextColor, whiteSpace: 'nowrap' }}>{tatLabel}</span>
                        </div>
                      )}
                    </td>
                    {/* Report Status */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                        <span style={{ fontSize: '14px', color: '#D8DCE8' }}>{reportStatusLabel}</span>
                      </div>
                    </td>
                    {/* Actions */}
                    <td style={{ padding: '0 10px', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button
                          className="wl-btn-read"
                          onClick={(e) => { e.stopPropagation(); handleOpenViewer(study); }}
                          style={{
                            height: '28px', padding: '0 16px',
                            background: '#2a5f5a', border: 'none', borderRadius: '6px',
                            fontSize: '12px', color: '#FFFFFF', cursor: 'pointer', fontWeight: 500,
                          }}
                        >Read</button>
                        <Tooltip title="Open in AfyaNX Workstation">
                          <button
                            className="wl-btn-workstation"
                            aria-label="Open in AfyaNX Workstation"
                            onClick={(e) => { e.stopPropagation(); handleOpenWorkstation(study); }}
                            style={{
                              height: '28px', width: '32px',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              background: 'transparent', border: '1px solid #2a5f5a', borderRadius: '6px',
                              color: '#7ecdc4', cursor: 'pointer', padding: 0,
                            }}
                          >
                            <Monitor size={14} />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── 5. Pagination Row ──────────────────────────────────────────────── */}
        {(() => {
          const totalPages = Math.max(1, Math.ceil(totalCount / pagination.pageSize));
          const cur = pagination.current;
          const goTo = (page) => setPagination(p => ({ ...p, current: Math.min(Math.max(1, page), totalPages) }));
          // Show up to 5 page number buttons centred around current page
          const pageNums = [];
          const half = 2;
          let start = Math.max(1, cur - half);
          let end   = Math.min(totalPages, start + 4);
          start = Math.max(1, end - 4);
          for (let i = start; i <= end; i++) pageNums.push(i);
          return (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderTop: '1px solid #3a3d44', background: '#23262d',
            }}>
              <span style={{ fontSize: '14px', color: '#FFFFFF' }}>{firstLoad ? 'Loading…' : <>Total&nbsp;{totalCount}&nbsp;studies</>}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  className="wl-page-btn"
                  disabled={cur <= 1}
                  onClick={() => goTo(cur - 1)}
                  onMouseDown={(e) => e.preventDefault()}
                  style={{ padding: '4px', background: 'none', border: 'none', borderRadius: '4px', color: cur <= 1 ? '#555' : '#9CA3AF', cursor: cur <= 1 ? 'default' : 'pointer', lineHeight: 1, outline: 'none' }}
                >
                  <ChevronLeft size={16} />
                </button>
                {pageNums.map(n => (
                  <button
                    key={n}
                    onClick={() => goTo(n)}
                    onMouseDown={(e) => e.preventDefault()}
                    style={{
                      padding: '4px 10px', borderRadius: '4px', border: 'none',
                      background: n === cur ? '#2a5f5a' : 'none',
                      color: n === cur ? '#FFFFFF' : '#9CA3AF',
                      fontSize: '14px', fontWeight: n === cur ? 600 : 400, cursor: 'pointer',
                      outline: 'none',
                    }}
                  >{n}</button>
                ))}
                <button
                  className="wl-page-btn"
                  disabled={cur >= totalPages}
                  onClick={() => goTo(cur + 1)}
                  onMouseDown={(e) => e.preventDefault()}
                  style={{ padding: '4px', background: 'none', border: 'none', borderRadius: '4px', color: cur >= totalPages ? '#555' : '#9CA3AF', cursor: cur >= totalPages ? 'default' : 'pointer', lineHeight: 1, outline: 'none' }}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', color: '#FFFFFF' }}>Per page:</span>
                <CustomSelect
                  value={pagination.pageSize}
                  onChange={(v) => setPagination({ current: 1, pageSize: Number(v) })}
                  height="28px"
                  openUp
                  style={{ minWidth: '100px' }}
                  options={[
                    { value: 5,  label: '5 / page' },
                    { value: 10, label: '10 / page' },
                    { value: 25, label: '25 / page' },
                    { value: 50, label: '50 / page' },
                  ]}
                />
              </div>
            </div>
          );
        })()}
      </div>
    </div>

    {/* ── Assignment-notes popover (fixed-positioned, outside table flow) ── */}
    {notePopover && (
      <>
        <div
          ref={notePopoverRef}
          className="wl-note-popover"
          onMouseEnter={handlePopoverEnter}
          onMouseLeave={handlePopoverLeave}
          style={{
            position: 'fixed',
            left: notePopover.x,
            top: notePopover.y,
            transform: notePopover.flipped ? 'translateX(-50%) translateY(-100%)' : 'translateX(-50%)',
            zIndex: 1100,
            background: '#1a1d23',
            border: '1px solid #2d4a47',
            borderTop:    notePopover.flipped ? '1px solid #2d4a47'   : '2px solid #1D9E75',
            borderBottom: notePopover.flipped ? '2px solid #1D9E75'   : '1px solid #2d4a47',
            borderRadius: '8px',
            padding: '12px 14px',
            maxWidth: '300px',
            minWidth: '180px',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.55)',
            pointerEvents: 'auto',
          }}
        >
          {/* Arrow caret — points toward the icon */}
          {notePopover.flipped ? (
            <div style={{
              position: 'absolute', bottom: '-7px', left: '50%', transform: 'translateX(-50%)',
              width: 0, height: 0,
              borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
              borderTop: '7px solid #1D9E75',
            }} />
          ) : (
            <div style={{
              position: 'absolute', top: '-7px', left: '50%', transform: 'translateX(-50%)',
              width: 0, height: 0,
              borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
              borderBottom: '7px solid #1D9E75',
            }} />
          )}
          <div style={{
            fontSize: '10px', fontWeight: 700, color: '#1D9E75',
            textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '7px',
            display: 'flex', alignItems: 'center', gap: '5px',
          }}>
            <MessageSquare size={11} />
            Assignment Notes
          </div>
          <div style={{
            fontSize: '13px', color: '#D1D5DB', lineHeight: 1.55,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            maxHeight: '160px', overflowY: 'auto',
          }}>
            {notePopover.text}
          </div>
        </div>
      </>
    )}
    </>
  );
};

const mapStateToProps = (state) => ({
  uploadInfoModal: state.system.uploadInfoModal,
  currentProject: state.studyList?.currentProject || {},
  users: state.system.users,
  profile: state.system.profile,
});

const mapDispatchToProps = {
  actionGetUsers,
  actionCreateSession,
};

export default connect(mapStateToProps, mapDispatchToProps)(
  withRouter(WorklistPage)
);
